import { appSessionState } from '../../api/connections';
import { openLocalDb } from '../../data/localDb';
import { openDb } from '../../lib/db';
import { createStore } from '../../lib/store';
import { player } from '../../player/engine';
import { CUPERTINO_CODEPOINTS } from '../icons/cupertinoCodepoints';
import { isDesktopLayout } from '../theme/layout';
import type { GateId } from './routes';
import { entryScreens } from './screens';

// Cold-start bootstrap (Flutter MyApp._bootstrapApp, spec 01 §5.1). Three tasks
// run in parallel while the startup screen shows:
// - core: open the IndexedDB databases (downloads/progress queue, history);
// - assets: load the UI fonts so the first frame never flashes fallback text
//   or invisible icons (the web stand-in for Flutter's audio-service step);
// - entry: download the lazy screen chunks the first frame draws (the gate,
//   or the tab/overlay/page/modal stack for the URL, plus the mini player),
//   so the splash hands over to a rendered screen instead of a blank canvas.
// None can fail the app: IndexedDB can be unavailable (private modes) or
// blocked by another tab's upgrade, a chunk can fail offline; each task is
// capped and errors are ignored. Settings, saved connections and the last
// played book are read synchronously at import.

export interface BootState {
  coreReady: boolean;
  assetsReady: boolean;
  entryReady: boolean;
  done: boolean;
}

export const bootStore = createStore<BootState>({ coreReady: false, assetsReady: false, entryReady: false, done: false });

const CORE_TIMEOUT_MS = 4000;
const ASSETS_TIMEOUT_MS = 2500;
const ENTRY_TIMEOUT_MS = 4000;

function capped(task: () => Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    let pending: Promise<unknown>;
    try {
      pending = task();
    } catch {
      pending = Promise.resolve();
    }
    pending.then(
      () => undefined,
      () => undefined,
    ).finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

function openDatabases(): Promise<unknown> {
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  return Promise.allSettled([openDb(), openLocalDb()]);
}

function loadFonts(): Promise<unknown> {
  if (typeof document === 'undefined' || !document.fonts?.load) return Promise.resolve();
  const icon = String.fromCodePoint(CUPERTINO_CODEPOINTS.house);
  return Promise.allSettled([
    document.fonts.load('22px CupertinoIcons', icon),
    document.fonts.load('500 14px "Inter Variable"'),
    document.fonts.load('700 16px "Inter Variable"'),
    document.fonts.load('700 20px "Merriweather Variable"'),
  ]);
}

/** The gate `AppNavigator` shows for the stored session (null when signed in). */
function startupGate(): GateId | null {
  const session = appSessionState();
  if (session === 'authenticated') return null;
  return session === 'noActiveConnectionConfigured' ? 'no-connections' : 'welcome';
}

/** react-router keeps the location state of the current entry in `history.state.usr`. */
function routerState(): unknown {
  const state: unknown = window.history.state;
  return state && typeof state === 'object' ? (state as Record<string, unknown>).usr ?? null : null;
}

function preloadEntry(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve();
  const { pathname, search, hash } = window.location;
  const screens = entryScreens({ pathname, search, hash, state: routerState() }, {
    gate: startupGate(),
    hasBook: player.store.get().itemId !== null,
    desktop: isDesktopLayout(),
  });
  return Promise.allSettled(screens.map((screen) => screen.preload()));
}

let started: Promise<void> | null = null;

/** Runs the bootstrap once; later calls return the same promise. */
export function startBootstrap(): Promise<void> {
  if (started) return started;
  const core = capped(openDatabases, CORE_TIMEOUT_MS).then(() => bootStore.set((s) => ({ ...s, coreReady: true })));
  const assets = capped(loadFonts, ASSETS_TIMEOUT_MS).then(() => bootStore.set((s) => ({ ...s, assetsReady: true })));
  const entry = capped(preloadEntry, ENTRY_TIMEOUT_MS).then(() => bootStore.set((s) => ({ ...s, entryReady: true })));
  started = Promise.all([core, assets, entry]).then(() => bootStore.set((s) => ({ ...s, done: true })));
  return started;
}

/**
 * The startup phase message (Flutter `_resolveStartupLoadingMessage`). The web
 * has no audio-service phase, so "Starting audio engine..." collapses into
 * "Opening your library..." and there is no fatal "Startup failed" state.
 */
export function startupMessage(state: BootState): string {
  if (!state.coreReady && !state.assetsReady) return 'Preparing your library...';
  return 'Opening your library...';
}
